import { useCallback, useEffect, useState } from "react";

export interface AccentPreset {
  id: string;
  label: string;
  /** HSL triple, no hsl() wrapper — matches the design token format. */
  hsl: string;
  swatch: string;
}

export const ACCENT_PRESETS: AccentPreset[] = [
  { id: "violet", label: "Violet", hsl: "268 83% 62%", swatch: "#8b5cf6" },
  { id: "amber", label: "Amber", hsl: "38 92% 55%", swatch: "#f0a52a" },
  { id: "emerald", label: "Emerald", hsl: "158 75% 43%", swatch: "#16b47f" },
  { id: "cyan", label: "Cyan", hsl: "190 90% 50%", swatch: "#0ec5e8" },
  { id: "rose", label: "Rose", hsl: "345 85% 60%", swatch: "#ef4370" },
  { id: "blue", label: "Blue", hsl: "220 90% 60%", swatch: "#3b82f6" },
];

const STORAGE_KEY = "mcg-accent";

export function applyAccent(id: string) {
  const preset = ACCENT_PRESETS.find((p) => p.id === id) ?? ACCENT_PRESETS[0];
  const root = document.documentElement;
  root.style.setProperty("--mcg-accent", preset.hsl);
  root.style.setProperty("--ring", preset.hsl);
  root.dataset.accent = preset.id;
}

export function getStoredAccent(): string {
  if (typeof window === "undefined") return ACCENT_PRESETS[0].id;
  return localStorage.getItem(STORAGE_KEY) || ACCENT_PRESETS[0].id;
}

/** User-chosen accent colour for the whole app, persisted locally. */
export function useAccentColor() {
  const [accent, setAccentState] = useState<string>(getStoredAccent);

  useEffect(() => {
    applyAccent(accent);
  }, [accent]);

  const setAccent = useCallback((id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setAccentState(id);
    applyAccent(id);
  }, []);

  /** Live hover preview without committing the choice. */
  const previewAccent = useCallback((id: string | null) => {
    applyAccent(id ?? getStoredAccent());
  }, []);

  return { accent, setAccent, previewAccent, presets: ACCENT_PRESETS };
}
